import { BASE_URL, CLIENT_DIAGNOSTICS_HOST, IS_CLIENT_DIAGNOSTICS_ENABLED } from '../config';
import { SSG_VERSION_FULL } from '../config/ssgVersion';
import { getContainerInfoReason, getContainerLabel, getShellNetworkType } from './browser/shellContainer';

export type ClientDiagnosticEventType =
  'ws_close'
  | 'ws_error'
  | 'connection_state'
  | 'liveness_probe'
  | 'liveness_timeout'
  | 'force_reconnect'
  | 'get_difference'
  | 'get_channel_difference'
  | 'get_history'
  | 'send_message_ack'
  | 'main_update_applied'
  | 'force_load_top_chats'
  | 'page_visibility_change'
  | 'media_download_start'
  | 'media_download_progress'
  | 'media_download_fail'
  | 'media_file_ref_repair'
  | 'get_sender_timeout'
  | 'media_queue_reject'
  | 'connection_reset'
  | 'stream_desync'
  | 'multitab_role'
  | 'multitab_resync'
  | 'event_counter'
  | 'media_slot_skip'
  | 'auth_step'
  | 'media_upload_start'
  | 'media_upload_done'
  | 'media_upload_fail'
  | 'message_received'
  | 'message_late'
  | 'chat_list_load'
  | 'chat_missing'
  | 'banned_rights_update'
  | 'chat_refetch'
  | 'message_list_render'
  | 'worker_ping_rtt'
  | 'worker_terminated'
  | 'worker_update_received'
  | 'container_info'
  | 'startup_phase'
  | 'worker_script_ready'
  | 'long_task'
  | 'transport_state'
  | 'shell_memory_warning'
  | 'http_trace';

export type ClientDiagnosticEvent = {
  type: ClientDiagnosticEventType | string;
  ts?: number;
  reason?: string;
  success?: boolean;
  duration?: number;
  error?: string;
  counterTag?: string;
};

const CLIENT_DIAGNOSTICS_UPLOAD_URL = `${CLIENT_DIAGNOSTICS_HOST}/api/client-diagnostics`;
const STORAGE_KEY = 'ssg_client_diagnostics_pending';
const DEVICE_ID_KEY = 'ssg_diag_device_id';
const SESSION_ID = createId();
const MAX_BUFFER_EVENTS = 200;
const MAX_UPLOAD_EVENTS = 120;
const UPLOAD_DEBOUNCE_MS = 5000;
const URGENT_UPLOAD_DEBOUNCE_MS = 1000;
const UPLOAD_BACKOFF_MS = [5000, 15000, 60000, 300000];

const SLOW_SEND_ACK_MS = 1500;
const SLOW_DIFFERENCE_MS = 2000;
const SLOW_MAIN_APPLY_MS = 500;
const SLOW_LIVENESS_PROBE_MS = 1500;
const STALLED_DOWNLOAD_MS = 3000;
const LARGE_UPDATE_BATCH = 50;

const MAX_EVENT_DURATION_MS = 30 * 60 * 1000;

const URGENT_UPLOAD_TYPES = new Set<ClientDiagnosticEventType>([
  'auth_step',
  'chat_missing',
  'ws_close',
  'ws_error',
  'force_reconnect',
  'liveness_timeout',
  'connection_state',
  'connection_reset',
  'message_late',
  'media_upload_fail',
  'worker_terminated',
]);

const DEDUP_WINDOW_MS = 30000;
const DEDUP_EVENT_TYPES = new Set<ClientDiagnosticEventType>([
  'media_download_progress',
  'media_download_start',
  'liveness_probe',
  'page_visibility_change',
  'force_reconnect',
]);

const buffer: ClientDiagnosticEvent[] = [];
const recentEventKeys = new Map<string, number>();

const eventCounters = new Map<string, { ok: number; fail: number }>();
let counterTimer: number | undefined;
let counterWindowSeq = 0;
const COUNTER_FLUSH_MS = 300000;
const COUNTER_PACK_MAX_CHARS = 160;
const DROPPED_COUNTER_KEY = 'diag_buffer_dropped';

const HIGH_VOLUME_TYPES = new Set<ClientDiagnosticEventType>(['message_received']);
const HIGH_VOLUME_SAMPLE_RATE = 0.05;

let hiddenMsSinceLoad = 0;
let hiddenSince = typeof document !== 'undefined' && document.hidden ? Date.now() : 0;

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenSince = Date.now();
    } else if (hiddenSince) {
      hiddenMsSinceLoad += Date.now() - hiddenSince;
      hiddenSince = 0;
    }
  });
}

const BLOCKING_TASK_MS = 1000;
const STARTUP_BLOCKING_TASK_MS = 200;
const STARTUP_WINDOW_MS = 10000;
const MAX_STARTUP_LONG_TASKS = 20;

let startupLongTaskCount = 0;

function observeLongTasks() {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const duration = Math.round(entry.duration);
        const isStartup = entry.startTime < STARTUP_WINDOW_MS;

        if (duration < (isStartup ? STARTUP_BLOCKING_TASK_MS : BLOCKING_TASK_MS)) return;

        if (isStartup) {
          if (startupLongTaskCount >= MAX_STARTUP_LONG_TASKS) return;
          startupLongTaskCount += 1;
        }

        recordClientDiagnosticEvent({
          type: 'long_task',
          reason: `since_load:${Math.round(entry.startTime)} startup:${isStartup ? 1 : 0}`,
          duration,
          success: false,
        });
      });
    }).observe({ entryTypes: ['longtask'] });
  } catch {
    // Not supported (WebKit); the counter path still records nothing here.
  }
}

export function getHiddenMsSinceLoad() {
  return hiddenMsSinceLoad + (hiddenSince ? Date.now() - hiddenSince : 0);
}

let currentUserId = '';
let uploadTimer: number | undefined;
let uploadTimerDueAt = 0;
let isUploading = false;
let isInited = false;
let uploadDisabled = !IS_CLIENT_DIAGNOSTICS_ENABLED;
let consecutiveUploadFailures = 0;

function isDiagnosticsUploadEnabled() {
  return !uploadDisabled && typeof window !== 'undefined';
}

export function initClientDiagnostics() {
  if (isInited || typeof window === 'undefined') return;
  isInited = true;

  recordClientDiagnosticEvent({
    type: 'container_info',
    reason: getContainerInfoReason(),
    success: true,
  });

  document.addEventListener('visibilitychange', () => {
    recordClientDiagnosticEvent({
      type: 'page_visibility_change',
      reason: document.hidden ? 'hidden' : 'visible',
    }, { upload: !document.hidden });

    if (document.hidden) {
      flushEventCounters();
      void flushClientDiagnostics();
    }
  });

  document.addEventListener('freeze', () => {
    flushEventCounters();
    void flushClientDiagnostics();
  });

  observeLongTasks();

  window.addEventListener('online', () => {
    recordClientDiagnosticEvent({ type: 'connection_state', reason: 'browser_online', success: true }, { upload: true });
  });

  window.addEventListener('offline', () => {
    recordClientDiagnosticEvent({ type: 'connection_state', reason: 'browser_offline', success: false }, { upload: true });
  });

  window.addEventListener('pagehide', () => {
    flushEventCounters();
    void flushClientDiagnostics();
  });
}

const AUTH_ERROR_CODES = [
  'AUTHORIZATION_SIGN_UP_REQUIRED', 'ACCOUNT_OCCUPIED', 'PHONE_NUMBER_OCCUPIED',
  'ACCOUNT_NOT_FOUND', 'ACCOUNT_REQUIRED', 'ACCOUNT_RESTRICTED', 'ACCOUNT_INVALID',
  'PASSWORD_INVALID', 'PASSWORD_MISMATCH', 'PASSWORD_REQUIRED', 'PASSWORD_EMPTY',
  'PASSWORD_NOT_MEET_RULE', 'CAPTCHA_CODE_EXPIRED', 'CAPTCHA_CODE_NOT_MATCH',
  'CAPTCHA_INVALID', 'INVITE_CODE', 'FLOOD_WAIT', 'TIMEOUT', 'NETWORK', 'CANCELED',
];

export function recordAuthDiagnostic(
  step: 'sign_in' | 'sign_up' | 'sign_out' | 'captcha' | 'session_restore',
  params: { success: boolean; duration?: number; error?: unknown; detail?: string },
) {
  recordClientDiagnosticEvent({
    type: 'auth_step',
    reason: `step:${step}${currentUserId ? '' : ' pre_login:1'}${params.detail ? ` ${params.detail}` : ''}`,
    success: params.success,
    duration: params.duration,
    error: params.success ? undefined : classifyAuthError(params.error),
  }, { upload: true });
}

function classifyAuthError(err: unknown) {
  const raw = (err instanceof Error ? err.message : String(err ?? '')).toUpperCase();
  return AUTH_ERROR_CODES.find((code) => raw.includes(code)) || 'OTHER';
}

export function setClientDiagnosticsUserId(userId?: string) {
  const previous = currentUserId;
  currentUserId = userId || '';

  if (currentUserId && currentUserId !== '0' && currentUserId !== previous) {
    scheduleUpload();
  }
}

export function recordClientDiagnosticEvent(
  event: ClientDiagnosticEvent,
  options: { upload?: boolean } = {},
) {
  if (event.type !== 'event_counter') {
    countEvent(event);
  }

  if (!options.upload && isCounterOnlyEvent(event)) {
    return;
  }

  if (HIGH_VOLUME_TYPES.has(event.type as ClientDiagnosticEventType)
    && Math.random() >= HIGH_VOLUME_SAMPLE_RATE) {
    return;
  }

  if (shouldDedupEvent(event)) {
    return;
  }

  buffer.push(sanitizeEvent(event));
  if (buffer.length > MAX_BUFFER_EVENTS) {
    const dropped = buffer.length - MAX_BUFFER_EVENTS;
    buffer.splice(0, dropped);
    const entry = eventCounters.get(DROPPED_COUNTER_KEY) || { ok: 0, fail: 0 };
    entry.fail += dropped;
    eventCounters.set(DROPPED_COUNTER_KEY, entry);
    ensureCounterTimer();
  }

  if (!isDiagnosticsUploadEnabled()) {
    return;
  }

  if (options.upload || shouldUploadForEvent(event) || shouldSampleForBaseline(event)) {
    scheduleUpload(URGENT_UPLOAD_TYPES.has(event.type as ClientDiagnosticEventType));
  }
}

function isCounterOnlyEvent(event: ClientDiagnosticEvent) {
  if (event.type === 'media_slot_skip') {
    return true;
  }

  if (event.type === 'message_list_render') {
    return event.counterTag === 'ok';
  }

  if (event.type === 'liveness_probe' && event.reason === 'pong') {
    return (event.duration || 0) < SLOW_LIVENESS_PROBE_MS;
  }

  if (event.type === 'media_download_progress') {
    if (!String(event.reason || '').startsWith('pct:0 ')) return true;
    return (event.duration || 0) < STALLED_DOWNLOAD_MS;
  }

  return false;
}

async function flushClientDiagnostics() {
  if (!isDiagnosticsUploadEnabled()) return;
  if (isUploading || typeof fetch !== 'function') return;

  const pending = readPendingEvents();
  if (!buffer.length && !pending.length) return;
  const pendingSlice = pending;
  const batch = buffer.splice(0, Math.max(0, MAX_UPLOAD_EVENTS - pendingSlice.length));
  const uploadEvents = pendingSlice.concat(batch);
  if (!uploadEvents.length) return;

  const resolvedUserId = currentUserId;
  if (!resolvedUserId || resolvedUserId === '0') {
    if (!writePendingEvents(uploadEvents)) {
      buffer.unshift(...batch);
    }
    return;
  }

  const payload = {
    userId: Number(resolvedUserId) || 0,
    deviceId: getDeviceId(),
    appVersion: getAppVersion(),
    platform: getPlatformLabel(),
    country: '',
    networkType: getNetworkType(),
    wssHost: getWssHost(),
    sessionId: SESSION_ID,
    clientTime: Date.now(),
    events: uploadEvents,
  };

  isUploading = true;
  try {
    const response = await fetch(CLIENT_DIAGNOSTICS_UPLOAD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        uploadDisabled = true;
        clearPendingEvents();
        return;
      }
      throw new Error(`HTTP_${response.status}`);
    }

    consecutiveUploadFailures = 0;
    clearPendingEvents();
  } catch {
    consecutiveUploadFailures += 1;
    if (!writePendingEvents(uploadEvents)) {
      buffer.unshift(...batch);
    }
  } finally {
    isUploading = false;
    if (isDiagnosticsUploadEnabled() && (buffer.length > 0 || consecutiveUploadFailures > 0)) {
      scheduleUpload();
    }
  }
}

function countEvent(event: ClientDiagnosticEvent) {
  const key = event.counterTag ? `${event.type}/${event.counterTag}` : String(event.type);
  const entry = eventCounters.get(key) || { ok: 0, fail: 0 };
  if (event.success === false) {
    entry.fail += 1;
  } else {
    entry.ok += 1;
  }
  eventCounters.set(key, entry);

  ensureCounterTimer();
}

function ensureCounterTimer() {
  if (counterTimer !== undefined || typeof window === 'undefined') return;

  counterTimer = window.setTimeout(() => {
    counterTimer = undefined;
    flushEventCounters();
  }, COUNTER_FLUSH_MS);
}

function flushEventCounters() {
  if (!eventCounters.size) return;

  const snapshot = Array.from(eventCounters.entries());
  eventCounters.clear();

  counterWindowSeq += 1;
  const seq = counterWindowSeq;
  const suffix = ` seq:${seq}`;

  let packed: string[] = [];
  let packedLength = 0;

  const emitPacked = () => {
    if (!packed.length) return;
    recordClientDiagnosticEvent({
      type: 'event_counter',
      reason: packed.join(' ') + suffix,
      success: true,
    });
    packed = [];
    packedLength = 0;
  };

  snapshot.forEach(([type, { ok, fail }]) => {
    const entry = `${type}:${ok}:${fail}`;
    if (packedLength + entry.length + 1 > COUNTER_PACK_MAX_CHARS) {
      emitPacked();
    }
    packed.push(entry);
    packedLength += entry.length + 1;
  });

  emitPacked();

  scheduleUpload();
}

function scheduleUpload(isUrgent = false) {
  if (!isDiagnosticsUploadEnabled()) {
    return;
  }

  if (uploadTimer !== undefined && consecutiveUploadFailures > 0) {
    return;
  }

  const delay = consecutiveUploadFailures > 0
    ? UPLOAD_BACKOFF_MS[Math.min(consecutiveUploadFailures - 1, UPLOAD_BACKOFF_MS.length - 1)]
    : (isUrgent ? URGENT_UPLOAD_DEBOUNCE_MS : UPLOAD_DEBOUNCE_MS);
  const dueAt = Date.now() + delay;

  if (uploadTimer !== undefined) {
    if (dueAt >= uploadTimerDueAt) {
      return;
    }
    window.clearTimeout(uploadTimer);
  }

  uploadTimerDueAt = dueAt;
  uploadTimer = window.setTimeout(() => {
    uploadTimer = undefined;
    uploadTimerDueAt = 0;
    void flushClientDiagnostics();
  }, delay);
}

function shouldUploadForEvent(event: ClientDiagnosticEvent) {
  if (event.type === 'event_counter') return true;
  if (event.type === 'transport_state') return true;
  if (event.type === 'http_trace') return true;
  if (event.type === 'auth_step') return true;
  if (event.type === 'message_late') return true;
  if (event.type === 'chat_missing') return true;
  if (event.type === 'banned_rights_update') return true;
  if (event.type === 'chat_refetch') return true;
  if (event.type === 'multitab_resync') return true;
  if (event.type === 'media_upload_fail') return true;
  if (event.success === false) return true;
  if (event.type === 'liveness_timeout') return true;
  if (event.type === 'liveness_probe' && event.reason === 'pong') {
    return (event.duration || 0) >= SLOW_LIVENESS_PROBE_MS;
  }
  if (event.type === 'liveness_probe' && event.reason === 'wss_handshake') return true;
  if (event.type === 'force_reconnect') return true;
  if (event.type === 'connection_reset') return true;
  if (event.type === 'media_download_fail') return true;
  if (event.type === 'media_download_progress') {
    return String(event.reason || '').startsWith('pct:0 ') && (event.duration || 0) >= STALLED_DOWNLOAD_MS;
  }
  if (event.type === 'ws_close' && String(event.reason || '').includes('burst:')) return true;
  if (event.type === 'ws_close' || event.type === 'ws_error') return true;
  if (event.type === 'message_list_render' && event.counterTag !== 'ok') return true;
  if (event.type === 'get_history' && (event.reason || '').includes('count=0')) return true;
  if (event.type === 'get_history' && event.counterTag === 'local_anchor') return true;
  if (event.type === 'get_history' && (event.duration || 0) >= 3000) return true;
  if (event.type === 'send_message_ack' && (event.duration || 0) >= SLOW_SEND_ACK_MS) return true;
  if (event.type === 'get_difference' && (event.duration || 0) >= SLOW_DIFFERENCE_MS) return true;
  if (event.type === 'get_channel_difference' && (event.duration || 0) >= SLOW_DIFFERENCE_MS) return true;
  if (event.type === 'main_update_applied' && (event.duration || 0) >= SLOW_MAIN_APPLY_MS) return true;
  return false;
}

const BASELINE_SAMPLE_RATE = 0.03;
const BASELINE_SAMPLED_TYPES = new Set<ClientDiagnosticEventType>([
  'send_message_ack',
  'get_difference',
  'get_channel_difference',
  'liveness_probe',
]);

function shouldSampleForBaseline(event: ClientDiagnosticEvent) {
  if (!BASELINE_SAMPLED_TYPES.has(event.type as ClientDiagnosticEventType)) return false;
  return Math.random() < BASELINE_SAMPLE_RATE;
}

function shouldDedupEvent(event: ClientDiagnosticEvent) {
  if (!DEDUP_EVENT_TYPES.has(event.type as ClientDiagnosticEventType)) {
    return false;
  }

  let key = `${event.type}:${event.reason || ''}:${event.duration || ''}`;
  if (event.type === 'force_reconnect') {
    const reason = String(event.reason || '');
    key = `force_reconnect:${reason.includes('alive-skip') ? 'alive-skip' : reason.split(':')[0]}`;
  }

  const now = Date.now();
  const lastAt = recentEventKeys.get(key);
  if (lastAt && now - lastAt < DEDUP_WINDOW_MS) {
    return true;
  }

  recentEventKeys.set(key, now);
  if (recentEventKeys.size > 200) {
    recentEventKeys.forEach((ts, dedupKey) => {
      if (now - ts > DEDUP_WINDOW_MS) {
        recentEventKeys.delete(dedupKey);
      }
    });
  }

  return false;
}

export function shouldRecordUpdateBatch(count: number, durationMs: number) {
  return durationMs >= SLOW_MAIN_APPLY_MS || count >= LARGE_UPDATE_BATCH;
}

function sanitizeEvent(event: ClientDiagnosticEvent): ClientDiagnosticEvent {
  const rawDuration = normalizeDuration(event.duration) || 0;
  const isClamped = rawDuration > MAX_EVENT_DURATION_MS;
  const reason = sanitizeText(event.reason) || '';

  return {
    type: event.type,
    ts: event.ts || Date.now(),
    reason: isClamped ? `${reason} clamped:1`.slice(0, 180) : reason,
    success: event.success ?? true,
    duration: isClamped ? MAX_EVENT_DURATION_MS : rawDuration,
    error: sanitizeText(event.error) || '',
  };
}

function sanitizeText(value?: string) {
  if (!value) return undefined;
  return String(value).replace(/[^\w .,:/@=|-]/g, '').slice(0, 180);
}

function normalizeDuration(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  return Math.max(0, Math.round(value));
}

function readPendingEvents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const events = JSON.parse(raw);
    if (!Array.isArray(events)) return [];
    return events.slice(-MAX_UPLOAD_EVENTS).map(sanitizeEvent);
  } catch {
    return [];
  }
}

function writePendingEvents(events: ClientDiagnosticEvent[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_UPLOAD_EVENTS)));
    return true;
  } catch {
    return false;
  }
}

function clearPendingEvents() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

function getDeviceId() {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = createId();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch {
    return '';
  }
}

function getAppVersion() {
  return SSG_VERSION_FULL;
}

function getPlatformLabel() {
  return getContainerLabel();
}

function getNetworkType() {
  const shellNetworkType = getShellNetworkType();
  if (shellNetworkType) return shellNetworkType;

  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

  if (connection?.type) return String(connection.type);
  if (connection?.effectiveType) return `eff-${connection.effectiveType}`;
  return 'unknown';
}

function getWssHost() {
  const baseUrl = BASE_URL || '';
  try {
    if (/^wss?:\/\//i.test(baseUrl)) {
      return new URL(baseUrl).host;
    }
    if (/^https?:\/\//i.test(baseUrl)) {
      return new URL(baseUrl).host;
    }
  } catch {
    // Fall through to string cleanup.
  }
  return baseUrl.replace(/^wss?:\/\//i, '').replace(/^https?:\/\//i, '').split('/')[0];
}

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
